import './RootLayout.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import ButtonAppBar from './Header';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { useEffect, useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { HeaderProvider } from '../../context/headerContext';
import { AttentionProvider } from '../../context/attentionContext';
import { PizzaBranchProvider } from '../../context/pizzaBranchContext';
import PizzaAttentionWatcher from '../Attention/PizzaAttentionWatcher';
import { useAuth } from '../../context/authContext';
import { useColorMode } from '../../context/colorModeContext';
import { createManagementTheme } from '../../theme/managementTheme';
import ThemeIntroductionModal from './ThemeIntroductionModal';
import AuthenticationModal from '../views/AuthenticationModal';

const MANAGEMENT_SUFFIX = 'Management';
const DEFAULT_DOCUMENT_TITLE = `FunZone - ${MANAGEMENT_SUFFIX}`;

function normalizePathname(pathname) {
  if (!pathname) return '/';
  const trimmed = String(pathname).trim();
  if (!trimmed) return '/';
  if (trimmed.length > 1 && trimmed.endsWith('/')) return trimmed.slice(0, -1);
  return trimmed;
}

function resolveViewTitle({ pathname, menuItems }) {
  const normalizedPath = normalizePathname(pathname);

  // Views that are not necessarily represented in the sidebar menu.
  const fallbackTitles = {
    '/': 'Login',
    '/login': 'Login',
    '/rfid-login': 'RFID Login',
    '/request-password-reset': 'Passwort zurücksetzen',
    '/reset-password': 'Passwort zurücksetzen',
    '/bookingSearch': 'Buchungssuche',
    '/mailTemplateEditor': 'Mailvorlage bearbeiten',
    '/switch-identity': 'Identität wechseln',
  };

  if (fallbackTitles[normalizedPath]) return fallbackTitles[normalizedPath];

  const items = Array.isArray(menuItems) ? menuItems : [];
  const match = items.find(i => !i?.isExternal && normalizePathname(i?.link) === normalizedPath);
  if (match?.name) return match.name;

  // Prefer a short, user-friendly title for the most frequented view.
  if (normalizedPath === '/bookings') return 'Buchungen';

  return null;
}

function TitleManager() {
  const location = useLocation();
  const auth = useAuth();

  const titlePrefix = useMemo(() => {
    const roles = auth?.currentUser?.roles;
    const isSuperadmin =
      Array.isArray(roles) && roles.some(r => String(r || '').trim().toLowerCase() === 'superadmin');
    if (!isSuperadmin) return '';

    const isStaging = import.meta.env.VITE_IS_STAGING === 'true';
    const isDev = import.meta.env.VITE_IS_DEV === 'true' || Boolean(import.meta.env.DEV);
    const isProdBuild = Boolean(import.meta.env.PROD);
    const isLive = isProdBuild && !isStaging && !isDev;

    const envLetter = isLive ? 'L' : isStaging ? 'S' : 'D';
    return `(${envLetter}) `;
  }, [auth?.currentUser?.roles]);

  const viewTitle = useMemo(() => {
    return resolveViewTitle({ pathname: location?.pathname, menuItems: auth?.menuItems });
  }, [location?.pathname, auth?.menuItems]);

  useEffect(() => {
    if (viewTitle) {
      document.title = `${titlePrefix}${viewTitle} - ${MANAGEMENT_SUFFIX}`;
      return;
    }
    document.title = `${titlePrefix}${DEFAULT_DOCUMENT_TITLE}`;
  }, [viewTitle, titlePrefix]);

  return null;
}

function AuthenticationModalHost() {
  const auth = useAuth();
  const config = auth?.authModalConfig;

  if (!auth?.showAuthModal || !config) return null;

  return (
    <AuthenticationModal
      isOpen={auth.showAuthModal}
      onClose={() => auth.setAuthModal?.(false)}
      mode={config.mode}
      eligibleUsers={config.eligibleUsers}
      requiredRights={config.requiredRights || []}
      onAuthenticationSuccess={config.onAuthenticationSuccess}
      onCancel={config.onCancel}
      isCounterDevice={auth.isCounterDevice}
      displayInfo={config.displayInfo || null}
    />
  );
}

function App() {
  const { resolvedPaletteMode } = useColorMode();
  const theme = useMemo(() => createManagementTheme(resolvedPaletteMode), [resolvedPaletteMode]);

  return (
    <>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <HeaderProvider>
          <AttentionProvider>
            <PizzaBranchProvider>
              <TitleManager />
              <ThemeIntroductionModal />
              <ButtonAppBar>
                <Outlet />
              </ButtonAppBar>
              <AuthenticationModalHost />
              <PizzaAttentionWatcher />
            </PizzaBranchProvider>
          </AttentionProvider>
        </HeaderProvider>
      </ThemeProvider>
    </>
  );
}

export default App;
